export function getCached(ttlMilliseconds: number | null = null) {
    if (ttlMilliseconds === null) ttlMilliseconds = 5 * 60 * 1000; // set 5-minutes as default cached time
    return function (
        target: any,
        propertyKey: string,
        descriptor: PropertyDescriptor
    ) {
        const originalGetter = descriptor.get;
        if (!originalGetter) {
            throw new Error(
                "The @getCached decorator can only be applied to the getters"
            );
        }

        const cacheKey = Symbol(`__cache_${propertyKey}`);
        const timestampKey = Symbol(`__cache_timestamp_${propertyKey}`);

        descriptor.get = function () {
            const ref = this as any;
            const now = Date.now();

            if (
                ref[timestampKey] !== undefined &&
                ref[cacheKey] !== undefined &&
                now - ref[timestampKey] < ttlMilliseconds
            ) {
                return ref[cacheKey];
            }

            const result = originalGetter.apply(this);
            ref[cacheKey] = result;
            ref[timestampKey] = now;
            return result;
        };
    };
}

export function Cached(ttlMilliseconds: number | null = null) {
    if (ttlMilliseconds === null) ttlMilliseconds = 5 * 60 * 1000; // set 5-minutes as default cached time
    return function (
        target: any,
        propertyKey: string,
        descriptor: PropertyDescriptor
    ) {
        const originalMethod = descriptor.value;
        if (typeof originalMethod !== "function") {
            throw new Error(
                "The @Cached decorator can only be applied to the methods"
            );
        }

        const cacheKey = Symbol(`__cache_${propertyKey}`);

        descriptor.value = function (...args: any[]) {
            const ref = this as any;
            if (!ref[cacheKey]) {
                ref[cacheKey] = new Map<
                    string,
                    { timestamp: number; value: any }
                >();
            }
            const cacheMap: Map<string, { timestamp: number; value: any }> =
                ref[cacheKey];

            const argsKey = JSON.stringify(args);
            const now = Date.now();

            if (
                cacheMap.has(argsKey) &&
                now - cacheMap.get(argsKey)!.timestamp < ttlMilliseconds
            ) {
                return cacheMap.get(argsKey)!.value;
            }

            const result = originalMethod.apply(this, args);
            cacheMap.set(argsKey, { value: result, timestamp: now });
            return result;
        };
    };
}
